import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import ActionDrawer from '../components/shared/ActionDrawer';
import { showSuccess, showError, showServerError } from '../../utils/toastHelper';

function CarrierModal({ isOpen, onClose, carrier, onSuccess }) {
  // Kargo firması CRUD'u sadece COMPANY_ADMIN/SUPER_ADMIN için açılır;
  // SHIPPING_STAFF read-only modda görür ve modal'a erişemez.
  const baseEndpoint = '/admin/carriers';
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [apiSecretChanged, setApiSecretChanged] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    apiKey: '',
    apiSecret: '',
    status: 'ACTIVE',
    customerCode: '',
    svcUsername: '',
    svcPassword: '',
    settings: ''
  });

  useEffect(() => {
    if (carrier) {
      const creds = carrier.settings?.credentials || {};

      let rawSettings = { ...carrier.settings };
      if (rawSettings.credentials) {
        const { customerCode, username, password, ...otherCreds } = rawSettings.credentials;
        if (Object.keys(otherCreds).length === 0) {
          delete rawSettings.credentials;
        } else {
          rawSettings.credentials = otherCreds;
        }
      }

      setFormData({
        name: carrier.name || '',
        code: carrier.code || '',
        apiKey: carrier.apiKey || '',
        apiSecret: '',
        status: carrier.status || 'ACTIVE',
        customerCode: creds.customerCode || '',
        svcUsername: creds.username || '',
        svcPassword: '',
        settings: Object.keys(rawSettings).length > 0 ? JSON.stringify(rawSettings, null, 2) : ''
      });
      setApiSecretChanged(false);
      setSvcPasswordChanged(false);
    } else {
      setFormData({
        name: '',
        code: '',
        apiKey: '',
        apiSecret: '',
        status: 'ACTIVE',
        customerCode: '',
        svcUsername: '',
        svcPassword: '',
        settings: ''
      });
      setApiSecretChanged(false);
      setSvcPasswordChanged(false);
    }
  }, [carrier, isOpen]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await apiRequest('/shipping/providers');
        if (response.success) {
          setProviders(response.providers);
        }
      } catch (error) {
        console.error('Failed to fetch providers:', error);
        showError('Kargo firmaları yüklenirken bir hata oluştu');
      }
    };

    if (isOpen) {
      fetchProviders();
    }
  }, [isOpen]);

  const handleProviderChange = (e) => {
    const selectedCode = e.target.value;
    const provider = providers.find(p => p.code === selectedCode);

    if (provider) {
      setFormData({
        ...formData,
        code: selectedCode,
        name: provider.name,
        customerCode: '',
        svcUsername: '',
        svcPassword: ''
      });
    } else {
      setFormData({ ...formData, code: selectedCode });
    }
  };

  const [svcPasswordChanged, setSvcPasswordChanged] = useState(false);

  const buildSettingsObject = () => {
    let settings = {};

    if (formData.settings) {
      try {
        settings = JSON.parse(formData.settings);
      } catch (e) {
        console.error('Invalid JSON settings:', e);
      }
    }

    if (formData.customerCode || formData.svcUsername || formData.svcPassword) {
      if (!settings.credentials) settings.credentials = {};
      if (formData.customerCode) settings.credentials.customerCode = formData.customerCode;
      if (formData.svcUsername) settings.credentials.username = formData.svcUsername;
      if (svcPasswordChanged && formData.svcPassword) settings.credentials.password = formData.svcPassword;
      else if (!carrier && formData.svcPassword) settings.credentials.password = formData.svcPassword;
    }

    return settings;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!formData.name || !formData.code) {
      showError('Lütfen gerekli alanları doldurun');
      return;
    }

    try {
      setLoading(true);
      const settings = buildSettingsObject();
      const endpoint = carrier ? `${baseEndpoint}/${carrier.id}` : baseEndpoint;
      const method = carrier ? 'PUT' : 'POST';

      const requestData = {
        name: formData.name,
        code: formData.code,
        status: formData.status,
        ...(formData.apiKey && { apiKey: formData.apiKey }),
        settings: settings
      };

      if (apiSecretChanged && formData.apiSecret) {
        requestData.apiSecret = formData.apiSecret;
      } else if (!carrier && formData.apiSecret) {
        requestData.apiSecret = formData.apiSecret;
      }

      const response = await apiRequest(endpoint, {
        method,
        data: requestData
      });

      if (response.success) {
        showSuccess(`Kargo firması başarıyla ${carrier ? 'güncellendi' : 'eklendi'}`);
        onSuccess();
      } else {
        throw new Error(response.message || `Kargo firması ${carrier ? 'güncellenirken' : 'eklenirken'} bir hata oluştu`);
      }
    } catch (error) {
      console.error('Failed to save carrier:', error);
      if (error.isAuthError) return;
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || `Kargo firması ${carrier ? 'güncellenirken' : 'eklenirken'} bir hata oluştu`);
      }
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div className="d-flex justify-content-end gap-2 w-100">
      <button
        type="button"
        className="btn btn-light border"
        onClick={onClose}
      >
        İptal
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={loading}
        onClick={handleSubmit}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Kaydediliyor...
          </>
        ) : (
          'Kaydet'
        )}
      </button>
    </div>
  );

  const needsCustomerCode = formData.code === 'AK';

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={carrier ? 'Kargo Firması Düzenle' : 'Yeni Kargo Firması Ekle'}
      width="700px"
      footer={footer}
    >
      <div className="carrier-form-content">
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <label className="form-label text-muted small fw-medium">Firma Adı*</label>
            <input
              type="text"
              className="form-control"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              readOnly={true}
              placeholder="Lütfen önce Firma Kodu seçiniz"
              style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
            />
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label text-muted small fw-medium">Firma Kodu*</label>
            <select
              className="form-select"
              value={formData.code}
              onChange={handleProviderChange}
              required
            >
              <option value="">Seçiniz</option>
              {providers.map(provider => (
                <option key={provider.code} value={provider.code}>
                  {provider.code} - {provider.name}
                </option>
              ))}
            </select>
            <div className="form-text mt-1 extra-small text-muted">
              <i className="fas fa-info-circle me-1"></i>
              Başka kargo firması talebiniz varsa lütfen program yöneticisi ile görüşün.
            </div>
          </div>



          <div className="col-12 col-md-6">
            <label className="form-label text-muted small fw-medium">API Kullanıcı Adı</label>
            <input
              type="text"
              className="form-control"
              value={formData.apiKey}
              onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
              placeholder="API kullanıcı adı"
              autoComplete="off"
            />
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label text-muted small fw-medium">API Şifre</label>
            <input
              type="password"
              className="form-control"
              value={formData.apiSecret}
              onChange={(e) => {
                setFormData({ ...formData, apiSecret: e.target.value });
                setApiSecretChanged(true);
              }}
              placeholder={carrier ? "••••••••" : "API şifre"}
              autoComplete="new-password"
            />
            {carrier && !apiSecretChanged && (
              <div className="form-text text-muted small">
                Boş bırakırsanız mevcut şifre korunur.
              </div>
            )}
          </div>

          {needsCustomerCode && (
            <>
              <div className="col-12">
                <hr className="my-1" />
                <div className="form-text text-muted small mb-2">
                  <i className="fas fa-info-circle me-1"></i>
                  Aras Kargo Raporlama (SVC) servisi için ek kimlik bilgileri
                </div>
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label text-muted small fw-medium">Müşteri Kodu</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.customerCode}
                  onChange={(e) => setFormData({ ...formData, customerCode: e.target.value })}
                  placeholder="Örn: 1932448851342"
                  autoComplete="off"
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label text-muted small fw-medium">SVC Kullanıcı Adı</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.svcUsername}
                  onChange={(e) => setFormData({ ...formData, svcUsername: e.target.value })}
                  placeholder="SVC kullanıcı adı"
                  autoComplete="off"
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label text-muted small fw-medium">SVC Şifre</label>
                <input
                  type="password"
                  className="form-control"
                  value={formData.svcPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, svcPassword: e.target.value });
                    setSvcPasswordChanged(true);
                  }}
                  placeholder={carrier ? "••••••••" : "SVC şifre"}
                  autoComplete="new-password"
                />
                {carrier && !svcPasswordChanged && (
                  <div className="form-text text-muted small">
                    Boş bırakırsanız mevcut şifre korunur.
                  </div>
                )}
              </div>
            </>
          )}

          <div className="col-12 col-md-6">
            <label className="form-label text-muted small fw-medium">Durum*</label>
            <select
              className="form-select"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              required
            >
              <option value="ACTIVE">Aktif</option>
              <option value="INACTIVE">Pasif</option>
            </select>
          </div>

          <div className="col-12">
            <label className="form-label text-muted small fw-medium">Ek Ayarlar (JSON)</label>
            <textarea
              className="form-control font-monospace"
              rows={5}
              value={formData.settings}
              onChange={(e) => setFormData({ ...formData, settings: e.target.value })}
              placeholder='{"isTestMode": true, "additionalOption": "value"}'
              style={{ fontSize: '0.85rem' }}
            />
            <div className="form-text text-muted small mt-2">
              <i className="fas fa-lightbulb text-warning me-1"></i>
              <strong>İpucu:</strong> Test ortamını kullanmak için <code>{"{\"isTestMode\": true}"}</code> değerini ekleyebilirsiniz.
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .carrier-form-content {
          margin: -1rem;
          padding: 1rem;
        }
        
        .form-control:focus {
          border-color: var(--kito-orange);
          box-shadow: 0 0 0 0.25rem rgba(232, 108, 58, 0.25);
        }
        
        @media (max-width: 767px) {
          .carrier-form-content {
            margin: -0.5rem;
            padding: 0.5rem;
          }

          .form-label {
            font-size: 0.85rem;
            margin-bottom: 0.25rem;
          }
          
          .form-control, .btn, .form-select {
            font-size: 0.85rem;
            padding: 0.35rem 0.5rem;
          }
        }
      `}</style>
    </ActionDrawer>
  );
}

export default CarrierModal; 